// SPDX-License-Identifier: Apache-2.0

// diff-utils — line-level diff between two strings.
//
// Used by `EditHistoryViewer` to produce the "+12/-3 lines" badge next to each
// historical snapshot. Hand-rolled, zero deps. The implementation is a
// Myers-style LCS over line arrays, then a forward walk that classifies each
// line as `equal | added | removed`.
//
// Time/space: O(NM) for the LCS matrix where N, M are line counts. We cap at
// MAX_LINES per side and return a coarse "too large to diff precisely" badge
// for inputs beyond that — markdown notes are typically far below the cap, so
// this is rarely hit. The cap exists so a Yjs snapshot of a multi-MB note
// doesn't lock the UI thread.

const MAX_LINES = 4096;

export interface DiffStats {
  added: number;
  removed: number;
  /** True if either side exceeded `MAX_LINES`; the counts are still
   *  representative of the truncated comparison. */
  truncated: boolean;
}

function splitLines(s: string): string[] {
  // Preserve empty trailing lines so a doc that loses its trailing blank
  // shows a removal rather than a no-op.
  if (s === '') return [];
  return s.split(/\r\n|\n|\r/);
}

/** Longest common subsequence DP. Returns the LCS length matrix. */
function lcsMatrix(a: string[], b: string[]): Uint16Array {
  const n = a.length;
  const m = b.length;
  const dp = new Uint16Array((n + 1) * (m + 1));
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i * (m + 1) + j] = (dp[(i - 1) * (m + 1) + (j - 1)] ?? 0) + 1;
      } else {
        const up = dp[(i - 1) * (m + 1) + j] ?? 0;
        const left = dp[i * (m + 1) + (j - 1)] ?? 0;
        dp[i * (m + 1) + j] = up > left ? up : left;
      }
    }
  }
  return dp;
}

/**
 * Compute the +added / -removed line counts between `before` and `after`.
 * O(NM) on line counts, capped at MAX_LINES per side.
 */
export function diffLines(before: string, after: string): DiffStats {
  let a = splitLines(before);
  let b = splitLines(after);
  let truncated = false;
  if (a.length > MAX_LINES) {
    a = a.slice(0, MAX_LINES);
    truncated = true;
  }
  if (b.length > MAX_LINES) {
    b = b.slice(0, MAX_LINES);
    truncated = true;
  }

  const n = a.length;
  const m = b.length;

  // Fast path: identical strings.
  if (n === m) {
    let same = true;
    for (let i = 0; i < n; i++) {
      if (a[i] !== b[i]) {
        same = false;
        break;
      }
    }
    if (same) return { added: 0, removed: 0, truncated };
  }

  const dp = lcsMatrix(a, b);
  const lcsLen = dp[n * (m + 1) + m] ?? 0;

  // added = lines in `b` not part of the LCS; removed = lines in `a` not part.
  return {
    added: m - lcsLen,
    removed: n - lcsLen,
    truncated,
  };
}

/** Format a `DiffStats` as a short badge, e.g. "+12/-3" or "no change". */
export function formatDiffBadge(d: DiffStats): string {
  if (d.added === 0 && d.removed === 0) return d.truncated ? '~' : '·';
  const parts: string[] = [];
  if (d.added > 0) parts.push(`+${d.added}`);
  if (d.removed > 0) parts.push(`-${d.removed}`);
  return parts.join('/') + (d.truncated ? '~' : '');
}
